import { useQuery } from '@apollo/client';
import * as R from 'ramda';
import { graphql } from '~/apollo/generated/v3';
import type { StudyAccessManagerPartsFragment } from '~/apollo/generated/v3/graphql';
import { SimplePanel } from '~/components/common/Panel';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { rejectNil } from '~/utils/common';
import type { RefetchQueries } from '~/utils/graphql';
import { CreateStudyAccessForm } from './create-study-access-form';
import { DeleteStudyAccess } from './delete-study-access';
import { Heading } from '~/components/common/Heading';

export const studyAccessManagerParts = graphql(`
  fragment studyAccessManagerParts on StudyAccess {
    id
    company {
      id
      name
    }
    project {
      id
      name
    }
  }
`);

export const STUDY_ACCESS_MANAGER_COMPANIES = graphql(`
  query StudyAccessManagerCompanies {
    companyList {
      ...companyParts
      studyAccess {
        ...studyAccessManagerParts
      }
      user {
        ...userParts
      }
    }
  }
`);

export type StudyAccessParent = 'study' | 'project';

type Props = {
  parentType: StudyAccessParent;
  parentId: number;
  studyAccess: StudyAccessManagerPartsFragment[];
  projectStudyAccess?: StudyAccessManagerPartsFragment[];
  refetchQueries: RefetchQueries;
};

export function StudyAccessManager({
  parentType,
  parentId,
  studyAccess,
  projectStudyAccess = [],
  refetchQueries,
}: Props) {
  const { data, loading } = useQuery(STUDY_ACCESS_MANAGER_COMPANIES);

  if (loading) return <SpinnerPlaceholder />;

  const companyList = data?.companyList ?? [];

  // Generate list of companies to select from that have not already been
  // granted access through the project or Study Access form

  const projectCompanyIds = projectStudyAccess
    .map(sa => sa.company?.id)
    .filter(rejectNil);

  const studyAccessCompanyIds = studyAccess
    .map(sa => sa.company?.id)
    .filter(rejectNil);

  const currentCompanyIds = R.union(projectCompanyIds, studyAccessCompanyIds);

  const availableCompanies = companyList
    .filter(c => !currentCompanyIds.includes(c.id))
    .sort(R.ascend(c => c.name));

  const sortedSA = studyAccess
    .slice()
    .sort(R.ascend(sa => sa.company?.name ?? ''));

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className={parentType === 'study' ? '' : 'lg:col-span-2'}>
        <SimplePanel title="Company Access">
          <div className="space-y-4">
            {sortedSA.length > 0 && (
              <table className="table w-full table-compact">
                <tbody>
                  {sortedSA.map(sa => (
                    <tr key={sa.id}>
                      <td>{sa.company?.name}</td>
                      <td className="text-right">
                        <DeleteStudyAccess
                          studyAccessId={sa.id}
                          companyName={
                            sa.company?.name ?? '(no company assigned)'
                          }
                          refetchQueries={refetchQueries}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <CreateStudyAccessForm
              parentType={parentType}
              parentId={parentId}
              availableCompanies={availableCompanies}
              refetchQueries={refetchQueries}
            />
          </div>
        </SimplePanel>
      </div>

      {parentType === 'study' && (
        <div>
          <Heading level={3}>Access granted through project</Heading>
          <table className="table table-compact w-full">
            <tbody>
              {projectStudyAccess.length > 0
                ? projectStudyAccess
                    .slice()
                    .sort(R.ascend(sa => sa.company?.name ?? ''))
                    .map(sa => (
                      <tr key={sa.company?.id}>
                        <td>{sa.company?.name}</td>
                      </tr>
                    ))
                : 'No companies have access'}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
